import Flickity from 'flickity'
import debounce from 'lodash/debounce'
import throttle from 'lodash/throttle'

import Component from '../core/Component'
import withMedia from '../core/withMedia'
import { toggleClass } from '../utils/dom'

export const STATUS = {
    READY: 'is-ready',
    HIDDEN: 'is-hidden',
    CENTER: 'is-center',
}

export const defaults = {
    media: null,
}

class Slider extends Component {
    constructor(element, options) {
        super(element, defaults)

        this.ref = {
            content: null,
            slides: [],
            dots: [],
            controls: null,
            buttonNext: null,
            buttonPrev: null,
        }

        this.state = {
            playing: false,
        }

        this.options = options

        this.current = 0
        this.timer = null

        this.dragging = false

        this.handleResize = throttle(this.handleResize, 100)
        this.handleWindowBlur = debounce(this.handleWindowBlur, 50)
    }

    prepare() {
        this.handleResize()

        if (this.ref.slides.length > 1) {
            this.element.classList.add(STATUS.READY)

            this.flickity = new Flickity(this.ref.content, {
                cellSelector: '[data-ref="slide"]',
                prevNextButtons: false,
                wrapAround: true,
                autoPlay: 5000,
                pageDots: false,
                resize: true,
                ...this.options,
                on: {
                    ready: function() {
                        this.off( 'uiChange', this.stopPlayer );
                        this.off( 'pointerDown', this.stopPlayer );
                    }
                }
            })

            this.ref.buttonNext?.addEventListener('click', this.handleNextClick)
            this.ref.buttonPrev?.addEventListener('click', this.handlePreviousClick)

            this.flickity.on('select', this.handleSelect)
            this.flickity.on('dragStart', this.handleDragStart)
            this.flickity.on('dragEnd', this.animate)
            this.flickity.on('settle', this.handleSettle)

            this.element.addEventListener('mouseenter', this.stop)
            this.element.addEventListener('mouseleave', this.animate)
            this.element.addEventListener('click', this.handleClick)

            window.addEventListener('resize', this.handleResize)
            window.addEventListener('blur', this.handleWindowBlur)

            this.handleSelect(0)
        }

        if (this.ref.slides.length <= 3) {
            this.ref.controls?.classList.add('is-mobile-visible')
        }

        if (this.ref.slides.length === 1) {
            this.ref.controls?.classList.add('is-one')
        }
    }

    destroy() {
        this.stop()
        this.element.classList.remove(STATUS.READY)
        this.ref.controls?.classList.remove('is-mobile-visible')
        this.flickity?.destroy()
        this.ref.buttonNext?.removeEventListener('click', this.handleNextClick)
        this.ref.buttonPrev?.removeEventListener('click', this.handlePreviousClick)
        this.element.removeEventListener('mouseenter', this.stop)
        this.element.removeEventListener('mouseleave', this.animate)
        this.element.removeEventListener('click', this.handleClick)
        window.removeEventListener('resize', this.handleResize)
        window.removeEventListener('blur', this.handleWindowBlur)
    }

    handleNextClick = (event) => {
        event.preventDefault()
        this.stop()
        this.flickity.next()
    }

    handlePreviousClick = (event) => {
        event.preventDefault()
        this.stop()
        this.flickity.previous()
    }

    handleWindowBlur = (event) => {
        this.stop()
    }

    handleResize = () => {
        this.flickity?.resize()
    }

    handleClick = (event) => {
        if (this.dragging) {
            event.stopPropagation()
            event.preventDefault()
        }
    }

    handleSelect = (current) => {
        if (this.ref.dots) {
            this.ref.dots.forEach((dot, index) => toggleClass(dot, 'is-active', index === current))
        }

        if (this.ref.index) {
            this.ref.index.textContent = current < 9 ? `0${current + 1}` : current + 1
        }

        this.ref.buttonPrev.classList.toggle(STATUS.HIDDEN, current === 0)
        this.ref.buttonNext.classList.toggle(STATUS.HIDDEN, current === this.flickity.slides.length - 1)
        this.ref.slides.forEach((slide, index)  => {
            const component = Component.getFromElement(slide)
            if (!component) {
                return
            }

            if (index === current) {
                component.onEnter()
            } else {
                component.onLeave()
            }
        })
        this.current = current
    }

    handleDragStart = () => {
        this.dragging = true
        this.stop()
    }

    handleSettle = () => {
        this.dragging = false
    }

    stop = () => {
        clearTimeout(this.timer)
        this.state.playing = false
    }

    next = () => {
        this.flickity.next()
    }
}

export default withMedia(Slider)
